import { auditActionLabel, auditMachineName } from './labels.ts';
import type { AuditEntry } from '@orgus/protocol';
import { Drawer, RelativeTime, StatusPill, formatAbsolute } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { DetailSection, FactGrid } from '../detail/FactGrid.tsx';
import { auditActorLabel } from '../audit-ops/model.ts';

/**
 * One audit entry in the shared detail drawer: the result as a pill in the header, who did
 * what where and when as a fact grid, the failure reason, and the entry as recorded folded away.
 */
export function AuditDialog({ entry, onClose }: { entry: AuditEntry | undefined; onClose: () => void }) {
  const machines = useOrgus((s) => s.machines);
  const ok = entry?.result === 'ok';
  return (
    <Drawer
      open={!!entry}
      onOpenChange={(v) => !v && onClose()}
      width={480}
      layout="rows"
      eyebrow={
        entry && (
          <>
            <span className="text-caption text-fg-subtle">Audit entry</span>
            <StatusPill status={ok ? 'done' : 'failed'} label={ok ? 'Succeeded' : 'Failed'} />
          </>
        )
      }
      title={entry ? `${auditActionLabel(entry.action)}: ${entry.target || 'No object'}` : 'Audit entry'}
      description={
        entry && (
          <FactGrid
            label="Entry facts"
            lines={[
              [
                { label: 'When', value: <RelativeTime ts={entry.at} />, title: formatAbsolute(entry.at) },
                { label: 'By', value: auditActorLabel(entry), title: auditActorLabel(entry) },
                { label: 'Machine', value: auditMachineName(entry.machineId, machines, 'Removed machine') },
              ],
            ]}
          />
        )
      }
    >
      {entry && (
        <div className="flex flex-col gap-6 px-6 py-4">
          {entry.message && (
            <DetailSection title={ok ? 'Message' : 'Why it failed'}>
              <p className="text-ui text-fg">{entry.message}</p>
            </DetailSection>
          )}
          <DetailSection title="Details, as recorded" collapsible>
            {/* Long values wrap inside the box, so nothing runs past its edge on a phone. */}
            <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-all rounded-control border border-border-subtle bg-sunken px-3 py-2 font-mono text-caption text-fg-muted">
              {JSON.stringify({ at: formatAbsolute(entry.at), target: entry.target, params: entry.params, id: entry.id }, null, 2)}
            </pre>
          </DetailSection>
        </div>
      )}
    </Drawer>
  );
}
